/**
 * gammaContextMapper.ts — MER Algo Footprints v7.1B — Phase 6.4
 *
 * Pure mapping functions that produce GammaUnifiedContext from all Gamma sources.
 *
 * This is the canonical adapter layer. Every Gamma consumer uses one of these
 * functions rather than independently interpreting Gamma state.
 *
 * ── Sources → GammaUnifiedContext ─────────────────────────────────────────────
 *   fromGammaSnapshot(snapshot)  — from pure engine output (GammaSnapshot)
 *   fromLiveSnapshot(snapshot)   — from server-side hook shape (LiveGammaSnapshot-like)
 *   unavailable(symbol, reason)  — canonical unavailable context
 *
 * ── Properties ────────────────────────────────────────────────────────────────
 * Pure: no side effects, no async, no React, no server
 * Domain-owned: lives in market/engines/gamma
 * Strongly typed: all fields accounted for
 *
 * Placement: src/domains/market/engines/gamma/gammaContextMapper.ts
 */

import type { GammaSnapshot, GammaRegimeV2, GammaBias } from './gammaTypes';
import type { GammaUnifiedContext } from './gammaInfluenceTypes';
import { interpretGammaV2 } from './gammaV2Interpreter';
import type { GammaV2Interpretation } from './gammaV2Interpreter';
import type { GammaLiquidityOutput } from './gammaLiquidityInterpreter';

// ─── Minimal GammaV2Output interface (subset used for mapping) ────────────────
// Full type lives in server/gamma/gammaPureEngineV2.js (CommonJS, server-only).
// We define only the fields gammaContextMapper needs so this file stays browser-safe.

export interface GammaV2ThesisHints {
  regimeHint:      'pinning' | 'unstable' | 'balanced';
  gammaPosture:    'positive_gamma' | 'negative_gamma' | 'neutral_gamma';
  nearestWallAbove: number | null;
  nearestWallBelow: number | null;
  flipZoneLow:      number | null;
  flipZoneHigh:     number | null;
  confidence:       number;
}

export interface GammaV2WallEntry {
  strike:     number;
  side:       'support' | 'resistance' | 'magnet' | 'void';
  strength:   number;
  netGamma:   number;
  expirations?: string[];
  hasGreeks?:   boolean;
}

export interface GammaV2OutputSlice {
  symbol:            string;
  asOf:              string;
  spot:              number;
  posture:           'positive_gamma' | 'negative_gamma' | 'neutral_gamma';
  postureScore:      number;
  postureRaw?:       number;
  confidence:        number;
  thesisHints:       GammaV2ThesisHints;
  estimatedFlipZone: { low: number | null; high: number | null; confidence: number };
  strongestWalls?:   GammaV2WallEntry[];
  voidZones?:        GammaV2WallEntry[];
  concentration?:    { top3Share: number; top5Share: number; crowdedness: number };
  riskFlags?:        string[];
  meta:              { provider: string; fallbackActive: boolean; signModel?: string; dteWeightMode?: string; oiFreshnessStatus?: string; oiCoveragePct?: number; greekCoveragePct?: number; contractsUsed?: number };
  computedAt:        number;
  isStale?:          boolean;
  reason?:           string | null;
}

// ─── Helper types ─────────────────────────────────────────────────────────────

/**
 * Minimal server-side snapshot shape (matches LiveGammaSnapshot from useGammaSnapshot).
 * Defined here without importing React to keep the domain clean.
 */
export interface GammaServerSnapshot {
  symbol:              string;
  available:           boolean;
  reason:              string | null;
  isStale:             boolean | null;
  computedAt:          number | null;
  dataFetchedAt?:      number | null;
  dataQuality:         'high' | 'medium' | 'low' | null;
  regime:              GammaRegimeV2 | null;
  bias:                GammaBias | null;
  gammaScore:          number | null;
  pinScore:            number | null;
  expansionScore:      number | null;
  directionalScore:    number | null;
  callWall:            number | null;
  putWall:             number | null;
  keyMagnetStrike:     number | null;
  flipZoneLow:         number | null;
  flipZoneHigh:        number | null;
  breakoutFuelUp?:     number | null;
  breakoutFuelDown?:   number | null;
  notes?:              string[];
  tradeBehaviorProfile?: { note: string } | null;
}

// ─── Mapping functions ────────────────────────────────────────────────────────

/**
 * Produces a canonical unavailable GammaUnifiedContext.
 * Use when no data exists or when the subsystem is not connected.
 */
export function gammaUnavailable(
  symbol: string,
  reason: string | null = null,
): GammaUnifiedContext {
  return {
    symbol,
    available:        false,
    reason,
    isStale:          null,
    computedAt:       null,
    dataQuality:      null,
    regime:           null,
    bias:             null,
    gammaScore:       null,
    pinScore:         null,
    expansionScore:   null,
    directionalScore: null,
    callWall:         null,
    putWall:          null,
    keyMagnetStrike:  null,
    flipZoneLow:      null,
    flipZoneHigh:     null,
    breakoutFuelUp:   null,
    breakoutFuelDown: null,
    behaviorNote:     null,
    notes:            [],
  };
}

/**
 * Maps a pure engine GammaSnapshot to GammaUnifiedContext.
 * Used by the scoring pipeline (gammaDerivation.ts produces GammaSnapshot).
 */
export function fromGammaSnapshot(
  snapshot: GammaSnapshot,
  opts?: { isStale?: boolean; reason?: string | null },
): GammaUnifiedContext {
  return {
    symbol:           snapshot.symbol,
    available:        true,
    reason:           opts?.reason ?? null,
    isStale:          opts?.isStale ?? false,
    computedAt:       snapshot.computedAt,
    dataQuality:      snapshot.dataQuality,
    regime:           snapshot.regime,
    bias:             snapshot.bias,
    gammaScore:       snapshot.gammaScore,
    pinScore:         snapshot.pinScore,
    expansionScore:   snapshot.expansionScore,
    directionalScore: snapshot.directionalScore,
    callWall:         snapshot.callWall,
    putWall:          snapshot.putWall,
    keyMagnetStrike:  snapshot.keyMagnetStrike,
    flipZoneLow:      snapshot.flipZoneLow,
    flipZoneHigh:     snapshot.flipZoneHigh,
    breakoutFuelUp:   snapshot.breakoutFuelUp,
    breakoutFuelDown: snapshot.breakoutFuelDown,
    behaviorNote:     snapshot.tradeBehaviorProfile?.note ?? null,
    notes:            snapshot.notes,
  };
}

/**
 * Maps a server-side snapshot (from /api/gamma/snapshot or useGammaSnapshot hook)
 * to GammaUnifiedContext.
 * Used by ThesisPanel and any other UI-sourced path.
 */
export function fromServerSnapshot(snap: GammaServerSnapshot): GammaUnifiedContext {
  if (!snap.available) {
    return gammaUnavailable(snap.symbol, snap.reason);
  }
  return {
    symbol:           snap.symbol,
    available:        true,
    reason:           snap.reason ?? null,
    isStale:          snap.isStale ?? null,
    computedAt:       snap.computedAt,
    dataQuality:      snap.dataQuality,
    regime:           snap.regime,
    bias:             snap.bias,
    gammaScore:       snap.gammaScore,
    pinScore:         snap.pinScore,
    expansionScore:   snap.expansionScore,
    directionalScore: snap.directionalScore,
    callWall:         snap.callWall,
    putWall:          snap.putWall,
    keyMagnetStrike:  snap.keyMagnetStrike,
    flipZoneLow:      snap.flipZoneLow,
    flipZoneHigh:     snap.flipZoneHigh,
    breakoutFuelUp:   snap.breakoutFuelUp  ?? null,
    breakoutFuelDown: snap.breakoutFuelDown ?? null,
    behaviorNote:     snap.tradeBehaviorProfile?.note ?? null,
    notes:            snap.notes ?? [],
  };
}

// ─── GammaV2Output → GammaUnifiedContext ─────────────────────────────────────

/**
 * Maps a GammaV2Output (ThetaData chain-state engine) to GammaUnifiedContext.
 *
 * GammaV2Output and GammaUnifiedContext use different field names and regime
 * vocabularies (v2 uses posture strings; v1 uses POSITIVE/NEGATIVE/NEUTRAL).
 * This adapter normalizes the shapes so downstream consumers are provider-agnostic.
 *
 * Mapping conventions:
 *   posture 'positive_gamma' → regime 'POSITIVE'
 *   posture 'negative_gamma' → regime 'NEGATIVE'
 *   posture 'neutral_gamma'  → regime 'NEUTRAL'
 *
 *   regimeHint 'pinning'     → bias 'PIN'
 *   regimeHint 'unstable'    → bias 'UPSIDE_EXPANSION' or 'DOWNSIDE_EXPANSION' (spot-relative)
 *   regimeHint 'balanced'    → bias 'BALANCED'
 *
 *   postureScore (0–100)     → gammaScore (0–100)
 *   confidence (0–1)         → pinScore / expansionScore (derived approximations)
 *
 * Fields with no v2 equivalent are set to null (breakoutFuelUp/Down, directionalScore).
 * Callers should treat these nulls as "not available from this source" rather than errors.
 *
 * @param v2  — GammaV2Output or a slice containing the required fields
 * @param opts — optional overrides for isStale and reason
 */
export function fromV2Output(
  v2: GammaV2OutputSlice,
  opts?: { isStale?: boolean; reason?: string | null },
): GammaUnifiedContext {
  if (!v2) return gammaUnavailable('', 'NO_DATA');

  // ── Run deterministic interpreter to get rich structural signals ──────────
  // The interpreter produces regime, bias, scores, signals, and reasoning
  // from the full v2 output. This replaces the previous thin approximations.
  let interp: GammaV2Interpretation | null = null;
  try {
    interp = interpretGammaV2({
      symbol:       v2.symbol,
      spot:         v2.spot,
      posture:      v2.posture,
      postureScore: v2.postureScore,
      postureRaw:   v2.postureRaw,
      confidence:   v2.confidence ?? 0,
      estimatedFlipZone: v2.estimatedFlipZone,
      strongestWalls:    v2.strongestWalls ?? [],
      concentration:     v2.concentration  ?? { top3Share: 0, top5Share: 0, crowdedness: 0 },
      riskFlags:         v2.riskFlags      ?? [],
      meta:              v2.meta           ?? {},
    });
  } catch {
    // Interpreter failure is non-fatal — fall back to thin mapping
    interp = null;
  }

  const conf = v2.confidence ?? 0;
  const stale = opts?.isStale ?? v2.isStale ?? false;

  // ── Map posture → regime ──────────────────────────────────────────────────
  // Use interpreter regime when available (more precise than posture alone);
  // fall back to direct posture mapping.
  const regime: GammaRegimeV2 =
    interp
      ? (interp.regime === 'unstable'  ? 'NEGATIVE'
       : interp.regime === 'pinning'   ? 'POSITIVE'
       :                                 'NEUTRAL')
      : v2.posture === 'positive_gamma' ? 'POSITIVE'
      : v2.posture === 'negative_gamma' ? 'NEGATIVE'
      :                                   'NEUTRAL';

  // ── Map bias ──────────────────────────────────────────────────────────────
  // Interpreter bias is directionally specific ('bullish'/'bearish'/'neutral').
  // Map to GammaUnifiedContext bias enum.
  const bias: GammaBias = interp
    ? (interp.directionalBias === 'bullish' ? 'UPSIDE_EXPANSION'
     : interp.directionalBias === 'bearish' ? 'DOWNSIDE_EXPANSION'
     :                                        'BALANCED')
    : (v2.thesisHints?.regimeHint === 'pinning' ? 'PIN' : 'BALANCED');

  // ── Scores — from interpreter when available ──────────────────────────────
  const pinScore       = interp ? interp.pinScore       : Math.round(conf * 30);
  const expansionScore = interp ? interp.expansionScore : Math.round(conf * 20);
  const directionalScore = interp ? interp.directionalScore : null;

  // ── Structure fields ──────────────────────────────────────────────────────
  const structure     = interp?.structure;
  const callWall      = structure?.nearestWallAbove ?? v2.thesisHints?.nearestWallAbove ?? null;
  const putWall       = structure?.nearestWallBelow ?? v2.thesisHints?.nearestWallBelow ?? null;
  const keyMagnetStrike = structure?.keyMagnetStrike ?? null;

  // ── Flip zone ─────────────────────────────────────────────────────────────
  const flipZoneLow  = interp?.flipZone.low  ?? v2.thesisHints?.flipZoneLow  ?? v2.estimatedFlipZone?.low  ?? null;
  const flipZoneHigh = interp?.flipZone.high ?? v2.thesisHints?.flipZoneHigh ?? v2.estimatedFlipZone?.high ?? null;

  // ── Breakout fuel — derived from interpreter signals and wall proximity ────
  // breakoutFuelUp/Down approximated from the expansion/breakout signals.
  // Positive = spot is positioned for an upside move; expressed as 0–100.
  const hasBreakoutUp   = interp?.signals.breakoutBias && interp.directionalBias === 'bullish';
  const hasBreakoutDown = interp?.signals.breakoutBias && interp.directionalBias === 'bearish';
  const breakoutFuelUp   = hasBreakoutUp   ? interp!.expansionScore : null;
  const breakoutFuelDown = hasBreakoutDown ? interp!.expansionScore : null;

  // ── Behavior note — use interpreter reasoning when available ──────────────
  const behaviorNote = interp
    ? interp.reasoning.slice(0, 2).join(' | ')
    : v2.riskFlags?.length
      ? `Risk flags: ${v2.riskFlags.slice(0, 3).join(', ')}`
      : null;

  // ── Data quality ──────────────────────────────────────────────────────────
  const dataQuality: 'high' | 'medium' | 'low' =
    conf >= 0.7 ? 'high' : conf >= 0.4 ? 'medium' : 'low';

  // ── Notes array ───────────────────────────────────────────────────────────
  const notes: string[] = [
    `source: ThetaData chain-state engine (v2)`,
    `provider: ${v2.meta?.provider ?? 'thetadata'}${v2.meta?.fallbackActive ? ' [FALLBACK]' : ''}`,
    `confidence: ${(conf * 100).toFixed(0)}%`,
  ];
  if (interp) {
    // Surface active signals as notes for downstream context
    const activeSignals = Object.entries(interp.signals)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (activeSignals.length > 0) notes.push(`signals: ${activeSignals.join(', ')}`);
    if (interp.regime !== 'balanced') notes.push(`regime: ${interp.regime}`);
  }

  return {
    symbol:           v2.symbol,
    available:        true,
    reason:           opts?.reason ?? v2.reason ?? null,
    isStale:          stale,
    computedAt:       v2.computedAt,
    dataQuality,
    regime,
    bias,
    gammaScore:       v2.postureScore ?? null,
    pinScore,
    expansionScore,
    directionalScore,
    callWall,
    putWall,
    keyMagnetStrike,
    flipZoneLow,
    flipZoneHigh,
    breakoutFuelUp,
    breakoutFuelDown,
    behaviorNote,
    notes,
  };
}

// ─── Thesis context projection ────────────────────────────────────────────────

/**
 * Projects a GammaUnifiedContext into the string lines used by the AI prompt.
 * Called by buildAIPrompt instead of the previous ad-hoc field selection.
 * Returns null when Gamma is unavailable (caller decides fallback text).
 */
/**
 * Projects a GammaUnifiedContext into the string lines used by the AI prompt.
 * Optionally accepts a GammaLiquidityOutput to surface liquidity-validated signals.
 * The liqOutput parameter is optional and never required — gamma context is always
 * surfaced even without liquidity context.
 */
export function gammaToThesisLines(
  ctx: GammaUnifiedContext,
  liqOutput?: GammaLiquidityOutput | null,
): string | null {
  if (!ctx.available) {
    const r = ctx.reason;
    if (r === 'NOT_WIRED') return 'Live gamma: provider not connected';
    return `Live gamma: unavailable (${r ?? 'unknown'})`;
  }
  const staleNote = ctx.isStale ? ' [STALE — treat as low-confidence context]' : '';
  const lines: string[] = [
    `Live gamma${staleNote}: Regime ${ctx.regime ?? '?'} | Bias ${ctx.bias ?? '?'}`,
    `  Scores — Pin:${ctx.pinScore ?? '?'} Expansion:${ctx.expansionScore ?? '?'} Directional:${ctx.directionalScore ?? '?'}`,
    `  Walls — Call:${ctx.callWall?.toFixed(2) ?? '?'} Put:${ctx.putWall?.toFixed(2) ?? '?'} Magnet:${ctx.keyMagnetStrike?.toFixed(2) ?? '?'}`,
  ];
  if (ctx.flipZoneLow !== null || ctx.flipZoneHigh !== null) {
    const low  = ctx.flipZoneLow?.toFixed(2)  ?? '?';
    const high = ctx.flipZoneHigh?.toFixed(2) ?? '?';
    lines.push(`  Flip zone: ${low}–${high}`);
  }
  if (ctx.breakoutFuelUp !== null || ctx.breakoutFuelDown !== null) {
    const up   = ctx.breakoutFuelUp   !== null ? `Up:${ctx.breakoutFuelUp}`   : null;
    const down = ctx.breakoutFuelDown !== null ? `Down:${ctx.breakoutFuelDown}` : null;
    lines.push(`  Breakout fuel — ${[up, down].filter(Boolean).join(' ')}`);
  }
  if (ctx.behaviorNote) lines.push(`  Context: ${ctx.behaviorNote}`);
  // Surface active signals from notes (populated by fromV2Output interpreter)
  const signalNote = ctx.notes.find(n => n.startsWith('signals:'));
  if (signalNote) lines.push(`  ${signalNote}`);

  // ── Liquidity-enhanced signals (when available) ────────────────────────────
  if (liqOutput) {
    const liq = liqOutput;
    const parts: string[] = [];
    if (liq.interactionSummary.dominantBehavior !== 'unclear') {
      parts.push(`behavior=${liq.interactionSummary.dominantBehavior}`);
    }
    if (liq.interactionSummary.compressionQuality !== 'low' || liq.signals.compressionFragile) {
      parts.push(`compression=${liq.interactionSummary.compressionQuality}`);
    }
    if (liq.interactionSummary.breakoutValidation !== 'unclear') {
      parts.push(`breakout=${liq.interactionSummary.breakoutValidation}`);
    }
    if (liq.interactionSummary.trapRisk !== 'low') {
      parts.push(`trapRisk=${liq.interactionSummary.trapRisk}`);
    }
    // Wall validation summary
    const fakeWalls = liq.wallValidation.filter(w => w.status === 'fake_risk');
    const weakWalls = liq.wallValidation.filter(w => w.status === 'weak');
    if (fakeWalls.length > 0) {
      parts.push(`fakeWallRisk:${fakeWalls.map(w => w.strike.toFixed(2)).join(',')}`);
    } else if (weakWalls.length > 0) {
      parts.push(`weakWalls:${weakWalls.map(w => w.strike.toFixed(2)).join(',')}`);
    }
    if (parts.length > 0) {
      lines.push(`  Gamma+Liq: ${parts.join(' | ')}`);
    }
  }

  return lines.filter(Boolean).join('\n');
}
